// src/hooks/audience/useIngestionFiles.js
import { useCallback, useEffect, useMemo, useState } from "react";
import Api from "../../utils/Api";
import {
  API_ROUTE_AUDIENCE_DATA_INGESTION,
  API_ROUTE_AUDIENCE_DATA_INGESTION_SUMMARY,
} from "../../routes";
import { toLocalDateTime } from "../../utils/helpers";

export function useIngestionFiles({
  year,
  monthIndex0, // 0..11 dans ton UI
  displayTypeId, // GUID
  take = 50,
  enabled = true,
  month, // optional: if not provided, fetch all months for the year
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  // API attend month=1..12
  const apiMonth = useMemo(
    () => (monthIndex0 != null ? monthIndex0 + 1 : null),
    [monthIndex0],
  );

  // Mapping API -> row DataTable
  const mapApiToRow = useCallback(
    (f) => ({
      id: f.audienceFileId,
      fileName: f.audienceFileName,
      totalLines: f.totalLines ?? "—",
      ingestedLines: f.ingestedLines ?? "—",
      status: f.audienceFileStatusName ?? "—",
      message: f.message ?? "—",
      startDate: f.uploadStartDate ? toLocalDateTime(f.uploadStartDate) : "—",
      endDate: f.uploadEndDate ? toLocalDateTime(f.uploadEndDate) : "—",
    }),
    [],
  );

  const canFetch =
    enabled && !!displayTypeId && year != null && apiMonth != null;

  const fetchFiles = useCallback(async () => {
    if (!canFetch) return;

    setLoading(true);
    setError(null);

    try {
      // Imported Files is per display type, not shared: Desktop and Mobile each keep
      // their own file for the month, so the summary must be scoped to whichever type
      // is currently selected.
      const resp = await Api.post(API_ROUTE_AUDIENCE_DATA_INGESTION_SUMMARY, {
        year,
        ...(apiMonth != null ? { month: apiMonth } : {}),
        displayTypeId,
        take,
      });

      const data = Array.isArray(resp.data) ? resp.data : [];
      setRows(data.map(mapApiToRow));
    } catch (e) {
      setError(e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [canFetch, year, apiMonth, displayTypeId, take, mapApiToRow]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const uploadFiles = useCallback(
    async (files) => {
      if (!displayTypeId) throw new Error("displayTypeId is required");
      if (year == null || apiMonth == null)
        throw new Error("year/month is required");

      setUploading(true);
      setError(null);

      try {
        const fd = new FormData();
        for (const file of files) {
          fd.append("Files", file);
        }
        fd.append("DisplayTypeId", displayTypeId);
        fd.append("Year", String(year));
        fd.append("Month", String(apiMonth));

        await Api.post(API_ROUTE_AUDIENCE_DATA_INGESTION, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        await fetchFiles();
      } catch (e) {
        setError(e);
        throw e;
      } finally {
        setUploading(false);
      }
    },
    [displayTypeId, year, apiMonth, fetchFiles],
  );

  return {
    rows,
    loading,
    uploading,
    error,
    refetch: fetchFiles,
    uploadFiles,
  };
}
